import { clsx } from 'clsx';
import { useState } from 'react';

import { PRESET_ART } from '../avatar/presets';
import { afterImageError, pictureView, type PictureView } from '../profile/picture';

type Picture = { readonly preset?: string | null; readonly url?: string | null } | null | undefined;

/**
 * Fixed classes per size rather than an inline style, so Tailwind sees them;
 * the initial's type scales with the circle.
 */
const SIZE = { 32: 'size-8 text-sm', 128: 'size-32 text-5xl' } as const;

/**
 * The learner's face (learner picture PRD §3), decorative at both sizes: at
 * 32 px the transcript's `sr-only` "You said" already names the line, at
 * 128 px the profile card's heading does. Hidden as well as `alt=""`, so the
 * initial is not read out as a stray letter.
 */
export function LearnerPicture({
  picture,
  displayName,
  learnerId,
  size,
}: {
  picture: Picture;
  displayName: string;
  learnerId: string;
  size: 32 | 128;
}) {
  // Keyed on the url that failed, so a new photo gets its own chance to load.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  let view: PictureView = pictureView(picture, displayName, learnerId);
  if (view.kind === 'photo' && view.url === failedUrl) view = afterImageError(view, displayName, learnerId);

  const box = clsx('block shrink-0 overflow-hidden rounded-full', SIZE[size]);
  switch (view.kind) {
    case 'preset': {
      const Art = PRESET_ART[view.slug];
      return (
        <span className={box} aria-hidden="true">
          <Art className="size-full" />
        </span>
      );
    }
    case 'photo': {
      const url = view.url;
      return (
        <img
          src={url}
          alt=""
          decoding="async"
          className={clsx(box, 'object-cover')}
          aria-hidden="true"
          onError={() => setFailedUrl(url)}
        />
      );
    }
    case 'initials':
      return (
        <span
          className={clsx(box, 'flex items-center justify-center font-semibold text-white')}
          style={{ backgroundColor: view.colour }}
          aria-hidden="true"
        >
          {view.grapheme}
        </span>
      );
  }
}
